import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ok: {
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.45)',
    icon: CheckCircle,
    label: 'SendGrid Healthy',
  },
  quota_exceeded: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.14)',
    border: 'rgba(245, 158, 11, 0.5)',
    icon: AlertTriangle,
    label: 'Quota Exceeded',
  },
  unauthorized: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.14)',
    border: 'rgba(239, 68, 68, 0.5)',
    icon: AlertCircle,
    label: 'API Key Invalid',
  },
  missing_scope: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.14)',
    border: 'rgba(245, 158, 11, 0.5)',
    icon: AlertTriangle,
    label: 'Missing Mail Send Permission',
  },
  not_configured: {
    color: '#94a3b8',
    bg: 'rgba(148, 163, 184, 0.15)',
    border: 'rgba(148, 163, 184, 0.5)',
    icon: AlertCircle,
    label: 'Not Configured',
  },
  error: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.14)',
    border: 'rgba(239, 68, 68, 0.5)',
    icon: AlertCircle,
    label: 'SendGrid Error',
  },
};

const SendGridHealthBadge = () => {
  const [health, setHealth] = useState({ status: 'checking' });
  const [loading, setLoading] = useState(true);

  const check = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/email/health`);
      setHealth(await res.json());
    } catch (e) {
      setHealth({ status: 'error', detail: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { check(); }, []);

  const cfg = STATUS_CONFIG[health.status] || STATUS_CONFIG.error;
  const Icon = cfg.icon;

  return (
    <div
      data-testid="sendgrid-health-badge"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.65rem',
        padding: '0.65rem 0.9rem',
        borderRadius: '10px',
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        marginBottom: '1rem',
        flexWrap: 'wrap',
      }}
    >
      {loading ? (
        <RefreshCw size={18} className="animate-spin" style={{ color: cfg.color }} />
      ) : (
        <Icon size={18} style={{ color: cfg.color, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ color: cfg.color, fontWeight: 800, fontSize: '0.9rem' }} data-testid="sendgrid-health-label">
          {loading ? 'Checking SendGrid…' : cfg.label}
        </div>
        {!loading && (
          <div style={{ color: '#cbd5e1', fontSize: '0.82rem', marginTop: '2px' }} data-testid="sendgrid-health-detail">
            {health.detail}
            {health.sender_email && (
              <> · Sender: <strong style={{ color: '#e2e8f0' }}>{health.sender_email}</strong></>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={check}
        data-testid="sendgrid-health-recheck"
        style={{
          background: 'transparent',
          border: `1px solid ${cfg.border}`,
          color: cfg.color,
          borderRadius: '8px',
          padding: '0.35rem 0.7rem',
          fontSize: '0.78rem',
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.3rem',
        }}
      >
        <RefreshCw size={13} /> Recheck
      </button>
    </div>
  );
};

export default SendGridHealthBadge;
